import React, { memo, useCallback, useEffect, useState } from 'react'
import { Alert, Modal, Skeleton } from 'antd'
import { DefaultJsonForm } from '../../../../../../components/v2/default-json-form'
import { convertData2JsonForm } from '../../../../../../shared/lib/convertData2JsonForm'
import { isFormDataRequired } from '../../../../../../../lib/utils/collections'
import fetchApi from '../../../../../../../lib/utils/fetch-api'
import fetch from 'isomorphic-fetch'
import {
  JsonFormSchema,
  PrintFormData,
} from '../../../../../orders/card/ui/modals/modal-print-prepare/types'

interface ClaimPrintPrepareModalProps {
  id: number | string
  template: null | { id: number; name: string }
  isModalPrintOpen: boolean
  onCancel?: () => void
}

export const ClaimPrintPrepareModal: React.FC<ClaimPrintPrepareModalProps> = memo(
  (props: ClaimPrintPrepareModalProps) => {
    const { id, template, isModalPrintOpen, onCancel } = props
    const [formData, setFormData] = useState<Record<string, unknown>>({})
    const [schema, setSchema] = useState<Record<string, unknown>>()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const downloadUrl = useCallback(
      async (url: string) => {
        try {
          const response = await fetch(url, {
            method: 'POST',
            body: JSON.stringify(formData),
            credentials: 'include',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          })

          const contentType = response.headers.get('Content-Type')
          if (contentType && contentType.includes('application/json')) {
            const content = await response.json()
            const errorMessages = Array.isArray(content?.errors)
              ? content.errors
                  .map((e: string | { detail?: string }) => (typeof e === 'string' ? e : e?.detail))
                  .filter(Boolean)
              : []
            const message = errorMessages.length
              ? errorMessages.join(' ')
              : content?.command
              ? `Неизвестная переменная в шаблоне: ${content.command}`
              : 'Не удалось получить документ.'
            Modal.error({
              title: 'Ошибка',
              content: message,
            })
            return
          }

          const blob = await response?.blob()
          if (!blob) return

          const disposition = response.headers.get('Content-Disposition')
          const filename = (() => {
            if (disposition && disposition.indexOf('attachment') !== -1) {
              const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition)
              if (matches != null && matches[1]) {
                return decodeURIComponent(matches[1].replace(/['"]/g, ''))
              }
            }
            return 'file.docx'
          })()

          const downloadAnchorNode = document.createElement('a')
          const urlObject = window.URL.createObjectURL(blob)
          downloadAnchorNode.setAttribute('href', urlObject)
          downloadAnchorNode.setAttribute('download', filename)
          document.body.appendChild(downloadAnchorNode)
          downloadAnchorNode.click()
          downloadAnchorNode.remove()
          window.URL.revokeObjectURL(urlObject)
          onCancel?.()
        } catch (err) {
          Modal.error({
            title: 'Ошибка',
            content: 'Не удалось скачать документ. Повторите попытку позже.',
          })
        }
      },
      [formData, onCancel],
    )

    const printTemplate = useCallback(() => {
      if (!template?.id) return
      downloadUrl(`/api/claim_print/${template.id}/${id}`)
    }, [downloadUrl, id, template?.id])

    useEffect(() => {
      if (!isModalPrintOpen || !template?.id) return

      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
      let isMounted = true

      const fetchData = async () => {
        setLoading(true)
        try {
          const data = await fetchApi(`/api/claim_print_prepare/${template.id}/${id}`, {
            signal: controller?.signal,
          })
          const converted = convertData2JsonForm(data) as unknown as {
            resultObject: PrintFormData
            resultSchema: JsonFormSchema
          }
          if (!isMounted) return
          setError(null)
          setFormData(converted.resultObject ?? {})
          setSchema(converted.resultSchema ?? {})
        } catch (err) {
          if (controller?.signal?.aborted) return
          setError('Не удалось загрузить данные формы. Попробуйте обновить страницу.')
        } finally {
          if (isMounted) setLoading(false)
        }
      }

      fetchData()
      return () => {
        isMounted = false
        controller?.abort()
      }
    }, [id, isModalPrintOpen, template?.id])

    const okHandler = useCallback(() => {
      if (!schema) return
      const isRequiredFields = isFormDataRequired(formData, schema)
      if (!isRequiredFields) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      }
      printTemplate()
    }, [formData, printTemplate, schema])

    const changeHandler = useCallback((data: { formData?: Record<string, unknown> }) => {
      setFormData(prev => ({ ...prev, ...(data.formData || {}) }))
    }, [])

    if (!isModalPrintOpen) return null
    if (!template?.id) return <Alert message="Неверный ID шаблона" type="error" showIcon />

    return (
      <Modal
        title={`Подготовка к печати: "${template.name}"`}
        onOk={okHandler}
        onCancel={onCancel}
        open={isModalPrintOpen}
        destroyOnHidden
        okButtonProps={{ disabled: loading || Boolean(error) }}
        cancelButtonProps={{ disabled: loading }}
        styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
        maskClosable={!loading}
        closable={!loading}
      >
        {loading ? (
          <Skeleton active />
        ) : error ? (
          <Alert type="error" message={error} showIcon />
        ) : (
          <DefaultJsonForm
            formData={formData}
            schema={schema ?? {}}
            onChange={changeHandler}
            orientation="horizontal"
          />
        )}
      </Modal>
    )
  },
)

ClaimPrintPrepareModal.displayName = 'ClaimPrintPrepareModal'
